"use client"

import { sdk } from "@lib/config"
import { readLeadForensicsVisitorId } from "@lib/util/lead-forensics-id"
import type { ConsentCategories } from "@modules/common/components/cookie-consent/consent-context"

type TrackInput = {
  cartId: string
  checkoutStep?: string | null
  consent: ConsentCategories | null
}

type PaymentFailureInput = TrackInput & {
  providerId?: string | null
  code?: string | null
  status?: string | null
  message?: string | null
}

export async function trackAbandonedCartActivity({
  cartId,
  checkoutStep,
  consent,
}: TrackInput) {
  if (!cartId) return

  fireLeadForensicsCartHit(cartId, consent)

  await sdk.client
    .fetch("/store/abandoned-carts/track", {
      method: "POST",
      body: buildPayload({ cartId, checkoutStep, consent }),
    })
    .catch(() => null)
}

// Register a Lead Forensics page-view that carries the cart id, fired from the
// visitor's own browser so LF attributes it to their real company by IP. The
// backend hydration matches this exact `/lf/cart/<cartId>` URL instead of
// guessing by URL+time (see the lead-forensics hydration step). Marketing-consent
// gated — window.track_load only exists once the LF loader has mounted — and
// fired once per cart per browser session.
function fireLeadForensicsCartHit(
  cartId: string,
  consent: ConsentCategories | null
) {
  if (typeof window === "undefined") return
  if (!cartId || !consent?.marketing) return
  if (typeof window.track_load !== "function") return

  const key = `aov_lf_cart_${cartId}`
  try {
    if (window.sessionStorage.getItem(key)) return
  } catch {
    // sessionStorage unavailable — fall through and just fire the hit.
  }

  const url = `${window.location.origin}/lf/cart/${encodeURIComponent(cartId)}`
  try {
    window.track_load(url, `Cart ${cartId}`)
    window.sessionStorage.setItem(key, "1")
  } catch {
    // best-effort
  }
}

export async function trackAbandonedCartPaymentFailed({
  cartId,
  checkoutStep,
  consent,
  providerId,
  code,
  status,
  message,
}: PaymentFailureInput) {
  if (!cartId) return

  fireLeadForensicsCartHit(cartId, consent)

  await sdk.client
    .fetch("/store/abandoned-carts/payment-failed", {
      method: "POST",
      body: {
        ...buildPayload({ cartId, checkoutStep, consent }),
        payment_snapshot: {
          provider_id: providerId ?? null,
          code: code ?? null,
          status: status ?? null,
          message: message ?? null,
        },
      },
    })
    .catch(() => null)
}

export async function trackAbandonedCartCompleted({
  cartId,
  orderId,
}: {
  cartId: string
  orderId?: string | null
}) {
  if (!cartId) return

  await sdk.client
    .fetch("/store/abandoned-carts/completed", {
      method: "POST",
      body: {
        cart_id: cartId,
        order_id: orderId ?? null,
      },
    })
    .catch(() => null)
}

function buildPayload({ cartId, checkoutStep, consent }: TrackInput) {
  const step = checkoutStep ?? currentCheckoutStep()
  return {
    cart_id: cartId,
    // Only send checkout_step when we actually have one. A null would clobber
    // the previously-captured step on the backend record (e.g. user reaches
    // ?step=payment then navigates off-checkout — the off-checkout ping should
    // not erase "payment").
    ...(step ? { checkout_step: step } : {}),
    last_url: typeof window !== "undefined" ? window.location.href : null,
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
    pixel_identifiers: collectPixelIdentifiers(consent),
    utm_data: collectUtmData(consent),
    consent_state: consent
      ? {
          analytics: consent.analytics,
          marketing: consent.marketing,
        }
      : null,
  }
}

function currentCheckoutStep() {
  if (typeof window === "undefined") return null
  const params = new URLSearchParams(window.location.search)
  return params.get("step")
}

function collectPixelIdentifiers(consent: ConsentCategories | null) {
  if (!consent?.analytics && !consent?.marketing) return null

  const cookies = readCookies()
  const google: Record<string, string> = {}
  const hubspot: Record<string, string> = {}
  const leadforensics: Record<string, string> = {}
  const clickIds: Record<string, string> = {}

  if (consent.analytics) {
    copyCookie(cookies, google, "_ga")
    copyCookie(cookies, google, "_gid")
  }

  if (consent.marketing) {
    ;["_gcl_au", "_gcl_aw", "_gcl_gb"].forEach((key) =>
      copyCookie(cookies, google, key)
    )
    copyCookie(cookies, hubspot, "hubspotutk")

    // Lead Forensics does not drop a first-party cookie on this domain — its
    // integration sends a fresh GUID per page-view to LF's capture endpoint.
    // The LeadForensics component patches that to read a persistent GUID from
    // localStorage; surface the same value here so abandoned-cart records
    // correlate to LF dashboard sessions.
    const lfVisitorId = readLeadForensicsVisitorId()
    if (lfVisitorId) {
      leadforensics.visitor_id = lfVisitorId
    }

    const params = new URLSearchParams(window.location.search)
    ;["gclid", "gbraid", "wbraid", "msclkid"].forEach((key) => {
      const value = params.get(key)
      if (value) clickIds[key] = value
    })
  }

  return {
    google,
    hubspot,
    leadforensics,
    click_ids: clickIds,
  }
}

function collectUtmData(consent: ConsentCategories | null) {
  if (!consent?.marketing || typeof window === "undefined") return null
  const params = new URLSearchParams(window.location.search)
  const data: Record<string, string> = {}

  params.forEach((value, key) => {
    if (key.startsWith("utm_")) {
      data[key] = value
    }
  })

  return Object.keys(data).length > 0 ? data : null
}

function readCookies() {
  if (typeof document === "undefined") return {}
  return document.cookie.split("; ").reduce<Record<string, string>>(
    (acc, part) => {
      const [key, ...value] = part.split("=")
      if (!key) return acc
      acc[decodeURIComponent(key)] = decodeURIComponent(value.join("="))
      return acc
    },
    {}
  )
}

function copyCookie(
  cookies: Record<string, string>,
  target: Record<string, string>,
  key: string
) {
  if (cookies[key]) {
    target[key] = cookies[key]
  }
}
